import {
  CLOUD_DOWNLOAD_FAILURES,
  type CloudDownloadFailure,
} from '../CloudDownloadError'

// Shared with the import picker page (militao/import-picker). Bytes travel, never a link or a token.

export const PICKER_MESSAGE_SOURCE = 'counselpro-import-picker'
export const APP_MESSAGE_SOURCE = 'counselpro-app'

const PICKER_FAILURES = [
  'sign-in-failed',
  'picker-failed',
  'provider-unreachable',
  'not-set-up',
] as const

export type PickerFailure = (typeof PICKER_FAILURES)[number]

// One per pick, made by the app and put in the picker URL. A message without it is from some other window.
const REQUEST_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

export const isRequestId = (value: unknown): value is string =>
  typeof value === 'string' && REQUEST_ID_PATTERN.test(value)

interface TransferredFile {
  name: string
  size: number
  bytes: ArrayBuffer
}

interface FailedFile {
  name: string
  size: number
  failure: CloudDownloadFailure
}

export type PickerFileMessage = TransferredFile | FailedFile

interface PickerMessageBase {
  source: typeof PICKER_MESSAGE_SOURCE
  requestId: string
}

// The picker sends ready when it needs a sign-in ticket from the app.
// It sends files once per batch, then done, so it never holds every file at once.
export type PickerMessage = PickerMessageBase &
  (
    | { type: 'ready' }
    | { type: 'files'; files: PickerFileMessage[] }
    | { type: 'done' }
    | { type: 'cancelled' }
    | { type: 'failed'; failure: PickerFailure }
  )

// The app's answer to ready. The ticket lets the picker ask lukita to finish one sign-in, once.
export interface TicketMessage {
  source: typeof APP_MESSAGE_SOURCE
  type: 'ticket'
  requestId: string
  ticket: string
}

type MessageFields = Record<string, unknown>

const isRecord = (value: unknown): value is MessageFields =>
  typeof value === 'object' && value !== null

const isOneOf = <Option extends string>(
  options: readonly Option[],
  value: unknown,
): value is Option => options.some((option) => option === value)

export const isByteCount = (value: unknown) =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

// Not instanceof: a buffer made in another window or realm fails it.
const isArrayBuffer = (value: unknown) =>
  Object.prototype.toString.call(value) === '[object ArrayBuffer]'

const hasNameAndSize = (file: MessageFields) =>
  typeof file.name === 'string' && isByteCount(file.size)

const isTransferredFile = (file: MessageFields) =>
  isArrayBuffer(file.bytes) && !('failure' in file)

const isFailedFile = (file: MessageFields) =>
  isOneOf(CLOUD_DOWNLOAD_FAILURES, file.failure) && !('bytes' in file)

const isPickerFileMessage = (file: unknown): file is PickerFileMessage =>
  isRecord(file) &&
  hasNameAndSize(file) &&
  (isTransferredFile(file) || isFailedFile(file))

const BODILESS_TYPES = ['ready', 'done', 'cancelled']

const hasValidBody = (message: MessageFields) => {
  if (isOneOf(BODILESS_TYPES, message.type)) return true
  if (message.type === 'failed') {
    return isOneOf(PICKER_FAILURES, message.failure)
  }
  if (message.type !== 'files' || !Array.isArray(message.files)) return false
  return message.files.every(isPickerFileMessage)
}

export const isPickerMessage = (data: unknown): data is PickerMessage =>
  isRecord(data) &&
  data.source === PICKER_MESSAGE_SOURCE &&
  isRequestId(data.requestId) &&
  hasValidBody(data)

export const isTicketMessage = (data: unknown): data is TicketMessage =>
  isRecord(data) &&
  data.source === APP_MESSAGE_SOURCE &&
  data.type === 'ticket' &&
  isRequestId(data.requestId) &&
  typeof data.ticket === 'string' &&
  data.ticket.length > 0

export const isTransferred = (
  file: PickerFileMessage,
): file is TransferredFile => 'bytes' in file
